/**
 * The cars, and the paperwork that lets each of them go out.
 *
 * The half of the fleet that has never had a screen. `FleetDocument.OwnerType` has modelled a
 * vehicle's documents since Phase 3 and `ShiftRules` refuses a shift on them — a car with no
 * insurance on record cannot be signed into — but nothing over HTTP could record one, so in
 * practice a firm's insurance certificates lived in a drawer and the refusal looked like a
 * bug in the driver app.
 *
 * A plate is normalised by the server: `CA 1234 AB` and `ca1234ab` are the same car. The
 * form therefore does not fight the person typing it.
 */

import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Pill,
  Section,
  Select,
  Sub,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '../../ui';
import { FeatureChips } from '../../vehicles/FeatureChips';
import { className, featureName, offeredFeatures } from '../../vehicles/words';
import { DocumentEditor } from './DocumentEditor';
import type { FleetCategory, FleetFeature, FleetVehicle, NewDocument, NewVehicle } from './api';

interface Props {
  vehicles: FleetVehicle[] | null;
  categories: FleetCategory[];
  features: FleetFeature[];
  busy: boolean;

  /** The page's last failure, repeated inside a dialog because the dialog covers the page. */
  error: string | null;
  onRegister: (vehicle: NewVehicle) => Promise<boolean>;
  onUpdate: (vehicleId: string, vehicle: NewVehicle) => Promise<boolean>;
  onSetActive: (vehicle: FleetVehicle, active: boolean) => void;
  onRecordDocument: (document: NewDocument) => Promise<boolean>;
  onRemoveDocument: (documentId: string) => void;
}

const emptyVehicle = {
  plate: '',
  make: '',
  model: '',
  colour: '',
  year: '',
  categoryCode: '',
  seats: '4',
  notes: '',
  featureCodes: [] as string[],
};

export function VehicleList({
  vehicles,
  categories,
  features,
  busy,
  error,
  onRegister,
  onUpdate,
  onSetActive,
  onRecordDocument,
  onRemoveDocument,
}: Props) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const form = useId();

  const [fields, setFields] = useState(emptyVehicle);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [papersFor, setPapersFor] = useState<string | null>(null);

  // Held by id, not by row: every mutation refetches, and a car kept by value would show
  // the documents it had before the one just recorded.
  const papers = vehicles?.find((vehicle) => vehicle.id === papersFor) ?? null;

  function startEdit(vehicle: FleetVehicle) {
    setEditing(vehicle.id);
    setAdding(false);
    setFields({
      plate: vehicle.plate,
      make: vehicle.make,
      model: vehicle.model,
      colour: vehicle.colour ?? '',
      year: vehicle.year === null ? '' : String(vehicle.year),
      categoryCode: vehicle.categoryCode,
      seats: String(vehicle.seats),
      notes: vehicle.notes ?? '',
      featureCodes: vehicle.featureCodes,
    });
  }

  function close() {
    setFields(emptyVehicle);
    setEditing(null);
    setAdding(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    const details: NewVehicle = {
      plate: fields.plate.trim(),
      make: fields.make.trim(),
      model: fields.model.trim(),
      colour: fields.colour.trim() === '' ? null : fields.colour.trim(),
      year: fields.year === '' ? null : Number(fields.year),
      categoryCode:
        fields.categoryCode === '' ? (categories[0]?.code ?? '') : fields.categoryCode,
      seats: Number(fields.seats),
      notes: fields.notes.trim() === '' ? null : fields.notes.trim(),
      featureCodes: fields.featureCodes,
    };

    const done =
      editing !== null ? await onUpdate(editing, details) : await onRegister(details);

    // Only now. The form used to empty itself on submit, so a refused plate took the make,
    // the model and everything else with it.
    if (done) close();
  }

  return (
    <Section
      title={t('fleet.vehicles.title')}
      description={t('fleet.vehicles.help')}
      actions={
        <Button
          size="sm"
          disabled={busy}
          onClick={() => {
            setFields(emptyVehicle);
            setEditing(null);
            setAdding(true);
          }}
        >
          <Icon name="plus" small />
          {t('fleet.vehicles.register')}
        </Button>
      }
    >
      {vehicles === null ? (
        <EmptyState>{t('app.loading')}</EmptyState>
      ) : vehicles.length === 0 ? (
        <EmptyState>{t('fleet.vehicles.none')}</EmptyState>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>{t('fleet.columns.plate')}</Th>
              <Th>{t('fleet.vehicles.makeModel')}</Th>
              <Th>{t('vehicles.classLabel')}</Th>
              <Th numeric>{t('fleet.columns.seats')}</Th>
              <Th>{t('fleet.columns.driver')}</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => (
              <Tr key={vehicle.id} className={vehicle.isActive ? undefined : 'text-ink-3'}>
                <Td>
                  <span className="flex items-center gap-1.5">
                    {vehicle.plate}

                    {vehicle.documents.some((d) => d.isExpired) && (
                      <Pill tone="danger" soft>
                        {t('fleet.documents.hasExpired')}
                      </Pill>
                    )}
                  </span>
                </Td>
                <Td wrap>
                  {vehicle.make} {vehicle.model}
                  {vehicle.colour !== null && <Sub block>{vehicle.colour}</Sub>}
                </Td>
                <Td wrap>
                  {className(
                    t,
                    vehicle.categoryCode,
                    categories.find((c) => c.code === vehicle.categoryCode)?.displayName,
                  )}
                  {vehicle.featureCodes.length > 0 && (
                    <Sub block>
                      {[...vehicle.featureCodes]
                        .sort(
                          (a, b) =>
                            features.findIndex((f) => f.code === a) -
                            features.findIndex((f) => f.code === b),
                        )
                        .map((code) => {
                          const feature = features.find((f) => f.code === code);

                          return featureName(t, code, feature?.displayName, feature?.namedByFirm);
                        })
                        .join(' · ')}
                    </Sub>
                  )}
                </Td>
                <Td numeric>{formats.count(vehicle.seats)}</Td>
                <Td wrap>{vehicle.driverName ?? t('fleet.vehicles.nobodyInIt')}</Td>
                <Td>
                  <span className="flex justify-end gap-1.5">
                    <Button size="sm" onClick={() => setPapersFor(vehicle.id)}>
                      {t('fleet.documents.title')}
                    </Button>

                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => startEdit(vehicle)}
                    >
                      {t('fleet.edit')}
                    </Button>

                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => onSetActive(vehicle, !vehicle.isActive)}
                    >
                      {vehicle.isActive ? t('fleet.vehicles.retire') : t('fleet.vehicles.restore')}
                    </Button>
                  </span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Dialog
        open={adding || editing !== null}
        title={editing !== null ? t('fleet.vehicles.editing') : t('fleet.vehicles.registering')}
        onClose={close}
        actions={
          <>
            <Button variant="quiet" onClick={close}>
              {t('fleet.cancel')}
            </Button>
            <Button variant="primary" type="submit" form={form} disabled={busy}>
              {editing !== null ? t('fleet.save') : t('fleet.vehicles.register')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {error !== null && <Banner tone="danger">{error}</Banner>}

          <form id={form} onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
            <Field id="plate" label={t('fleet.vehicles.plate')} help={t('fleet.vehicles.plateHelp')}>
              <Input
                value={fields.plate}
                onChange={(event) => setFields({ ...fields, plate: event.target.value })}
                required
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field id="make" label={t('fleet.vehicles.make')}>
                <Input
                  value={fields.make}
                  onChange={(event) => setFields({ ...fields, make: event.target.value })}
                  required
                />
              </Field>

              <Field id="model" label={t('fleet.vehicles.model')}>
                <Input
                  value={fields.model}
                  onChange={(event) => setFields({ ...fields, model: event.target.value })}
                  required
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field id="colour" label={t('fleet.vehicles.colour')} help={t('fleet.vehicles.colourHelp')}>
                <Input
                  value={fields.colour}
                  onChange={(event) => setFields({ ...fields, colour: event.target.value })}
                />
              </Field>

              <Field id="year" label={t('fleet.vehicles.year')}>
                <Input
                  type="number"
                  min={1950}
                  max={2100}
                  value={fields.year}
                  onChange={(event) => setFields({ ...fields, year: event.target.value })}
                />
              </Field>
            </div>

            <Field id="categoryCode" label={t('fleet.vehicles.category')} help={t('fleet.vehicles.categoryHelp')}>
              <Select
                value={
                  fields.categoryCode === '' ? (categories[0]?.code ?? '') : fields.categoryCode
                }
                onChange={(event) => setFields({ ...fields, categoryCode: event.target.value })}
              >
                {categories.map((category) => (
                  <option key={category.code} value={category.code}>
                    {className(t, category.code, category.displayName)}
                  </option>
                ))}
              </Select>
            </Field>

            {offeredFeatures(features, fields.featureCodes).length > 0 && (
              <Field
                id="featureCodes"
                label={t('fleet.vehicles.features')}
                help={t('fleet.vehicles.featuresHelp')}
              >
                <FeatureChips
                  features={features}
                  chosen={fields.featureCodes}
                  label={t('fleet.vehicles.features')}
                  onChange={(change) =>
                    setFields((current) => ({ ...current, featureCodes: change(current.featureCodes) }))
                  }
                />
              </Field>
            )}

            <Field id="seats" label={t('fleet.vehicles.seatsLabel')} help={t('fleet.vehicles.seatsHelp')}>
              <Input
                type="number"
                min={1}
                max={16}
                value={fields.seats}
                onChange={(event) => setFields({ ...fields, seats: event.target.value })}
                required
              />
            </Field>

            <Field id="notes" label={t('fleet.vehicles.notes')}>
              <Textarea
                value={fields.notes}
                onChange={(event) => setFields({ ...fields, notes: event.target.value })}
              />
            </Field>
          </form>
        </div>
      </Dialog>

      {papers !== null && (
        <DocumentEditor
          ownerType="Vehicle"
          ownerId={papers.id}
          ownerName={papers.plate}
          documents={papers.documents}
          busy={busy}
          error={error}
          onRecord={onRecordDocument}
          onRemove={onRemoveDocument}
          onClose={() => setPapersFor(null)}
        />
      )}
    </Section>
  );
}
